import { devLog, devError } from '../utils/devLogger';
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../auth/AuthContext';

const formatDisplayDate = (value) => {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return 'Not scheduled';
  }
  return date.toLocaleDateString();
};

const normalizeRecordDate = (value) => {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
};

const AttendanceView = ({
  attendanceRecords = [],
  modules = [],
  pendingSessions = [],
  attendanceSummary = null,
  onAttendanceSubmitted = null
}) => {
  const { userProfile } = useAuth();
  const [selectedSession, setSelectedSession] = useState(null);
  const [signatureText, setSignatureText] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const moduleMap = useMemo(() => {
    const map = new Map();
    modules.forEach((module) => {
      map.set(module.id, module.moduleName || module.title || module.id);
    });
    return map;
  }, [modules]);

  const records = useMemo(() => {
    if (!attendanceRecords || attendanceRecords.length === 0) {
      return [];
    }

    const deduped = new Map();
    attendanceRecords.forEach((record) => {
      const date = normalizeRecordDate(record.date);
      const dayKey = date
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
        : 'unknown-date';
      const key = record.sessionId || `${record.moduleId || 'unknown-module'}_${dayKey}`;
      const normalized = {
        ...record,
        date,
        markedAt: normalizeRecordDate(record.markedAt)
      };
      const existing = deduped.get(key);
      const existingTime = existing?.markedAt?.getTime?.() || existing?.date?.getTime?.() || 0;
      const nextTime = normalized?.markedAt?.getTime?.() || normalized?.date?.getTime?.() || 0;
      if (!existing || nextTime >= existingTime) {
        deduped.set(key, normalized);
      }
    });

    return Array.from(deduped.values()).sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));
  }, [attendanceRecords]);

  const pending = useMemo(() => (
    (pendingSessions || []).slice().sort((a, b) => {
      const aDate = normalizeRecordDate(a?.scheduledDate)?.getTime?.() || 0;
      const bDate = normalizeRecordDate(b?.scheduledDate)?.getTime?.() || 0;
      return aDate - bDate;
    })
  ), [pendingSessions]);

  const summary = useMemo(() => {
    if (attendanceSummary) {
      return attendanceSummary;
    }

    const markedSessions = records.filter(
      (record) => !['not_marked', 'incomplete', 'self_paced', 'other'].includes(record.status)
    );
    const presentCount = records.filter((record) => record.status === 'present').length;
    const excusedCount = records.filter((record) => record.status === 'excused').length;
    const attendanceRate = markedSessions.length > 0
      ? Math.round(((presentCount + excusedCount) / markedSessions.length) * 100)
      : 0;

    return {
      totalSessions: records.length,
      markedSessions: markedSessions.length,
      presentCount,
      attendanceRate
    };
  }, [attendanceSummary, records]);

  const openConfirmationModal = (session) => {
    setSelectedSession(session);
    setSignatureText(userProfile?.displayName || '');
    setSubmitError('');
    setSuccessMessage('');
  };

  const closeConfirmationModal = () => {
    if (submitting) return;
    setSelectedSession(null);
    setSignatureText('');
    setSubmitError('');
  };

  const handleSubmitConfirmation = async (event) => {
    event.preventDefault();

    if (!selectedSession?.id) {
      setSubmitError('A valid session is required.');
      return;
    }

    if (signatureText.trim().length < 2) {
      setSubmitError('Please enter your signature before submitting.');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError('');
      const functions = getFunctions();
      const submitLearnerAttendanceConfirmation = httpsCallable(functions, 'submitLearnerAttendanceConfirmation');
      await submitLearnerAttendanceConfirmation({
        sessionId: selectedSession.id,
        signatureText: signatureText.trim(),
        sessionDate: selectedSession.scheduledDate || null
      });

      setSuccessMessage(`Attendance register completed for ${formatDisplayDate(selectedSession.scheduledDate)}.`);
      setSelectedSession(null);
      setSignatureText('');

      if (typeof onAttendanceSubmitted === 'function') {
        await onAttendanceSubmitted();
      }
    } catch (error) {
      devError('AttendanceView: failed to submit attendance confirmation', error);
      setSubmitError(error?.message || 'Failed to submit attendance register.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
        <div>
          <h4 className="mb-1">Attendance Overview</h4>
          <p className="text-muted mb-0">
            Review completed attendance, see which registers are still outstanding, and complete
            learner attendance confirmations directly in the LMS.
          </p>
        </div>
        <Badge bg={pending.length > 0 ? 'warning' : 'success'} pill>
          {pending.length > 0 ? `${pending.length} Outstanding` : 'Up to Date'}
        </Badge>
      </div>

      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
          {successMessage}
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body>
              <div className="text-muted small mb-1">Completed Registers</div>
              <div className="fs-3 fw-semibold">{summary.markedSessions || 0}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body>
              <div className="text-muted small mb-1">Outstanding Registers</div>
              <div className="fs-3 fw-semibold">{pending.length}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100 shadow-sm border-0">
            <Card.Body>
              <div className="text-muted small mb-1">Attendance Rate</div>
              <div className="fs-3 fw-semibold">{summary.attendanceRate || 0}%</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm border-0 mb-4">
        <Card.Header className="bg-white">
          <h5 className="mb-0">Outstanding Attendance Registers</h5>
        </Card.Header>
        <Card.Body>
          {pending.length === 0 ? (
            <Alert variant="success" className="mb-0">
              All currently requested attendance registers have been completed.
            </Alert>
          ) : (
            <Row className="g-3">
              {pending.map((session) => {
                const sessionTitle = session.title || session.sessionTitle || session.name || moduleMap.get(session.moduleId) || 'Session';
                return (
                  <Col xl={6} key={session.id}>
                    <Card className="h-100 border-0 bg-light">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start gap-3">
                          <div>
                            <h6 className="mb-1">{sessionTitle}</h6>
                            <div className="small text-muted">
                              Date: {formatDisplayDate(session.scheduledDate)}
                            </div>
                            <div className="small text-muted">
                              Facilitator: {session.facilitatorName || session.facilitator || 'Not assigned'}
                            </div>
                            <div className="small text-muted">
                              Cohort: {session.cohortName || session.cohortId || userProfile?.cohortName || 'Current cohort'}
                            </div>
                          </div>
                          <Badge bg="warning">Register Outstanding</Badge>
                        </div>
                        <div className="d-grid mt-3">
                          <Button onClick={() => openConfirmationModal(session)}>
                            Complete Register
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card.Body>
      </Card>

      <Card className="shadow-sm border-0">
        <Card.Header className="bg-white">
          <h5 className="mb-0">Attendance History</h5>
        </Card.Header>
        <Card.Body>
          {records.length === 0 ? (
            <Alert variant="info" className="mb-0">
              Once attendance is marked or you complete a requested register, it will appear here.
            </Alert>
          ) : (
            <Table striped bordered hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Module / Session</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr key={record.id}>
                    <td>{moduleMap.get(record.moduleId) || record.sessionTitle || record.moduleId || record.sessionId || 'Attendance record'}</td>
                    <td>{record.date ? record.date.toLocaleDateString() : 'Unknown'}</td>
                    <td className="text-capitalize">{String(record.status || 'unknown').replace(/_/g, ' ')}</td>
                    <td className="text-capitalize">{String(record.source || 'manual').replace(/_/g, ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={Boolean(selectedSession)} onHide={closeConfirmationModal} centered>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>Complete Attendance Register</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitConfirmation}>
          <Modal.Body>
            {selectedSession && (
              <>
                <Alert variant="info">
                  Confirm your attendance details below and provide your digital signature to submit
                  this register.
                </Alert>

                <div className="mb-3">
                  <div><strong>Name:</strong> {userProfile?.displayName || 'Learner'}</div>
                  <div><strong>ID Number:</strong> {userProfile?.idNumber || 'Not linked'}</div>
                  <div><strong>Session Date:</strong> {formatDisplayDate(selectedSession.scheduledDate)}</div>
                  <div><strong>Programme:</strong> {userProfile?.programName || userProfile?.program || 'Assigned programme'}</div>
                  <div><strong>Facilitator:</strong> {selectedSession.facilitatorName || selectedSession.facilitator || 'Not assigned'}</div>
                </div>

                <Form.Group>
                  <Form.Label>Digital Signature</Form.Label>
                  <Form.Control
                    type="text"
                    value={signatureText}
                    onChange={(event) => setSignatureText(event.target.value)}
                    placeholder="Type your full name as your signature"
                    disabled={submitting}
                    required
                  />
                  <Form.Text className="text-muted">
                    Typing your full name confirms that the attendance details above are correct.
                  </Form.Text>
                </Form.Group>

                {submitError && (
                  <Alert variant="danger" className="mt-3 mb-0">
                    {submitError}
                  </Alert>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeConfirmationModal} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Submitting...
                </>
              ) : (
                'Submit Register'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default AttendanceView;
